import React, {useState} from 'react';

import {createAppFromManifestLegacy} from 'benchling-apps/BenchlingAppActions';
import {BENCHLING_APPS_WEB_URL} from 'benchling-apps/BenchlingAppDAO';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import FieldError from 'ui-library/FieldError';
import FileChooser from 'ui-library/FileChooser';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import Modal from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';

export interface Props {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

// TODO (BNCH-92479): remove this component when we migrate all legacy apps
export default function CreateBenchlingAppFromManifestModalLegacy(props: Props): JSX.Element {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  async function handleSubmit(): Promise<void> {
    if (uploadedFile == null) {
      props.onRequestSubmit();
      return;
    }
    const taskId = TrayTaskActions.createTrayTask({
      heading: 'Uploading app manifest',
    });
    try {
      const importUrl = await ImportUtil.upload(uploadedFile);
      if (importUrl) {
        const data = {location: importUrl};
        const app = await createAppFromManifestLegacy(data);
        TrayTaskActions.updateTrayTask(taskId, {
          progress: TrayTaskConstants.SUCCESS_PROGRESS,
          subheading: `Successfully created app ${app.name} from manifest`,
          duration: 3,
        });

        const benchlingAppPageURL = `${BENCHLING_APPS_WEB_URL}/${app.id}`;
        navigateToURL(benchlingAppPageURL);
      }
    } catch (err) {
      if (net.isServerRequestError(err)) {
        TrayTaskActions.updateTrayTask(taskId, {
          progress: TrayTaskConstants.FAILURE_PROGRESS,
          error:
            notifications.getFriendlyError(err) ??
            'Something went wrong uploading the manifest. Please try again',
        });
      } else {
        throw err;
      }
    } finally {
      props.onRequestSubmit();
    }
  }

  function isUploadedFileInvalid(): boolean {
    if (uploadedFile == null) {
      return true;
    }
    const maybeFileType = uploadedFile.name.split('.').pop();
    return !(maybeFileType === 'yaml' || maybeFileType === 'yml');
  }

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      title="Create an app from manifest"
      buttons={[
        {
          text: 'Create',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: isUploadedFileInvalid(),
        },
      ]}
    >
      <Row>
        <Col lg={12}>
          <p>
            Create a credentialed app in this workspace and use a manifest file to add your app's basic
            settings and custom configurations.
          </p>
          {!uploadedFile ? (
            <FileChooser
              onSelectFiles={(...files: Array<File>) => {
                if (files.length === 1) {
                  setUploadedFile(files[0]);
                }
              }}
              accept=".yaml,.yml"
            />
          ) : (
            <FileList>
              <FileRow
                file={uploadedFile}
                onRequestRemove={() => {
                  setUploadedFile(null);
                }}
              />
            </FileList>
          )}
          {uploadedFile && isUploadedFileInvalid() && (
            <FieldError message="Uploaded file must have suffix .yaml or .yml" />
          )}
        </Col>
      </Row>
    </Modal>
  );
}
